import React from "react";
import { Alert, Avatar, Button, Drawer, Empty, Spin, Tag } from "antd";
import type { AdminStore, VendorKyc } from "types/adminStore";
import type { StoreApprovalItem } from "../types";
import martEmptyImg from "assets/images/mart-empty.png";

export type StoreReviewDrawerProps = {
  open: boolean;
  item: StoreApprovalItem | null;
  store?: AdminStore | null;
  kyc?: VendorKyc | null;
  kycLoading?: boolean;
  kycError?: string | null;
  onClose: () => void;
  onApprove: (item: StoreApprovalItem) => void;
  onDecline: (item: StoreApprovalItem) => void;
  /** When true, hide Approve/Decline footer actions (read-only review) */
  hideActions?: boolean;
  approveLoading?: boolean;
  declineLoading?: boolean;
  actionsDisabled?: boolean;
};

const PRIVACY_TEXT =
  "We collect and use your data to verify store ownership and process approvals. Your information may be shared with relevant authorities as required by law. We implement security measures to protect your data; however, no method of transmission over the internet is 100% secure.";

const readString = (...values: unknown[]) => {
  const value = values.find(
    (candidate): candidate is string =>
      typeof candidate === "string" && candidate.trim().length > 0
  );

  return value?.trim() || "";
};

const formatDate = (value?: string) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const KycDocumentCard: React.FC<{
  title: string;
  imageUrl?: string;
}> = ({ title, imageUrl }) => {
  return (
    <div className="store-review-doc-card">
      <div className="store-review-doc-title">{title}</div>
      {imageUrl ? (
        <a href={imageUrl} target="_blank" rel="noreferrer">
          <img
            src={imageUrl}
            alt={title}
            onError={(event) => {
              if (!event.currentTarget.src.endsWith(martEmptyImg)) {
                event.currentTarget.src = martEmptyImg;
              }
            }}
          />
        </a>
      ) : (
        <span className="store-review-doc-placeholder">Not uploaded</span>
      )}
    </div>
  );
};

export const StoreReviewDrawer: React.FC<StoreReviewDrawerProps> = ({
  open,
  item,
  store,
  kyc,
  kycLoading = false,
  kycError,
  onClose,
  onApprove,
  onDecline,
  hideActions = false,
  approveLoading = false,
  declineLoading = false,
  actionsDisabled = false,
}) => {
  if (!item) return null;

  const initials = item.ownerName
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

  const status = readString(store?.status, item.activeStatus, item.status, "—");
  const approvalStatus = readString(store?.approval_status, item.status, "—");
  const ownerName = readString(kyc?.user?.name, item.ownerName, "—");
  const storeFields = [
    { label: "Store Name", value: item.storeName },
    { label: "Store Status", value: status },
    { label: "Approval Status", value: approvalStatus },
    { label: "Market", value: readString(store?.market?.name, item.market) || "—" },
    {
      label: "Market Location",
      value: readString(store?.market?.location, item.marketLocation) || "—",
    },
    { label: "Address / Location", value: item.location || "—" },
  ];
  const ownerFields = [
    { label: "Name", value: ownerName },
    { label: "Email", value: readString(kyc?.user?.email, item.ownerEmail) || "—" },
    { label: "Phone Number", value: readString(kyc?.user?.phone, item.ownerPhone) || "—" },
    { label: "BVN Number", value: readString(kyc?.bvn_number, item.bvnNumber) || "—" },
    { label: "KYC Created At", value: formatDate(kyc?.created_at) },
  ];

  const errorMessage = kycError
    ? kycError.includes("Vendor id is missing")
      ? kycError
      : "Unable to load KYC details."
    : "";
  const errorDescription =
    kycError && !kycError.includes("Vendor id is missing") ? kycError : undefined;

  return (
    <Drawer
      title={null}
      placement="right"
      onClose={onClose}
      open={open}
      width={460}
      className="store-review-drawer"
      styles={{ body: { padding: 0 } }}
    >
      <div className="store-review-drawer-inner">
        <div className="store-review-header">
          <div className="store-review-header-pattern" />
          <div className="store-review-profile">
            <div
              className="store-review-logo"
              style={{ backgroundColor: item.logoColor || "#FFF3E0" }}
            >
              <img
                src={item.logoUrl || martEmptyImg}
                alt={item.storeName}
                onError={(event) => {
                  if (!event.currentTarget.src.endsWith(martEmptyImg)) {
                    event.currentTarget.src = martEmptyImg;
                  }
                }}
              />
            </div>
            <div className="store-review-profile-text">
              <div className="store-review-store-name">{item.storeName}</div>
              <div className="store-review-owner">
                <Avatar
                  size={20}
                  style={{
                    background: item.ownerAvatarColor || "#E8F5E9",
                    color: "#1a1a1a",
                    fontSize: 10,
                  }}
                >
                  {initials}
                </Avatar>
                <span>{item.ownerName}</span>
              </div>
              {hideActions && (
                <Tag color="success" className="store-review-approved-tag">
                  Approved
                </Tag>
              )}
            </div>
          </div>
        </div>

        <div className="store-review-section">
          <div className="store-review-section-label">Privacy Policy</div>
          <p className="store-review-privacy">{PRIVACY_TEXT}</p>
        </div>

        <div className="store-review-section">
          <div className="store-review-section-label">Store Info</div>
          <div className="store-review-owner-grid">
            {storeFields.map(({ label, value }) => (
              <div key={label} className="store-review-owner-row">
                <span className="store-review-owner-label">{label}:</span>
                <span className="store-review-owner-value">{value}</span>
              </div>
            ))}
          </div>
        </div>

        {kycLoading ? (
          <div className="store-review-state">
            <Spin size="large" />
            <span>Loading KYC details...</span>
          </div>
        ) : errorMessage ? (
          <div className="store-review-section">
            <Alert
              type="error"
              showIcon
              message={errorMessage}
              description={errorDescription}
            />
          </div>
        ) : !kyc ? (
          <div className="store-review-section">
            <Empty description="No KYC details found for this vendor." />
          </div>
        ) : (
          <>
            <div className="store-review-section">
              <div className="store-review-section-label">KYC Documents</div>
              <div className="store-review-docs">
                <KycDocumentCard title="BVN" imageUrl={kyc.bvn} />
                <KycDocumentCard title="NIN Front" imageUrl={kyc.nin_front} />
                <KycDocumentCard title="NIN Back" imageUrl={kyc.nin_back} />
              </div>
            </div>

            <div className="store-review-section">
              <div className="store-review-section-label">Owner Info</div>
              <div className="store-review-owner-grid">
                {ownerFields.map(({ label, value }) => (
                  <div key={label} className="store-review-owner-row">
                    <span className="store-review-owner-label">{label}:</span>
                    <span className="store-review-owner-value">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {!hideActions && (
          <div className="store-review-footer">
            <Button
              className="store-review-footer-decline"
              onClick={() => onDecline(item)}
              loading={declineLoading}
              disabled={actionsDisabled}
            >
              Decline
            </Button>
            <Button
              type="primary"
              className="store-review-footer-approve"
              onClick={() => onApprove(item)}
              loading={approveLoading}
              disabled={actionsDisabled}
            >
              Approve
            </Button>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default StoreReviewDrawer;
